import './styles.scss';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';

import checkout from '../../../assets/draws/checkout.png';
import player from '../../../assets/draws/player.png';
import challenges from '../../../assets/draws/challenges.png';
import chat from '../../../assets/draws/chat.svg';
import ebooks from '../../../assets/draws/ebooks.png';


import { ReactComponent as LinesTexture } from '../../../assets/textures/lines.svg';

import Badge from '../../../components/badge';

import { ReactComponent as OceanProtocolIcon } from '../../../assets/icons/ocean-protocol.svg';

const benefits = [
    {
        title: 'Checkout nativo',
        description: 'Venda seus produtos em uma plataforma projetada para profissionais no nicho fitness.',
        image: checkout,
        alt: 'Checkout nativo'
    },
    {
        title: 'Player de vídeos',
        description: 'Hospede seus conteúdos, gratuitos ou pagos, em um player robusto, nativo, e sem custo adicional.',
        image: player, // Substitua por imagem específica quando disponível
        alt: 'Player de vídeos'
    },
    {
        title: 'Desafios gamificados',
        description: 'Aumente o engajamento da sua comunidade com desafios gamificados, onde os participantes pontuam postando suas atividades físicas',
        image: challenges, // Substitua por imagem específica quando disponível
        alt: 'Desafios gamificados'
    },
    {
        title: 'Chat',
        description: 'Promova a interação entre os membros da sua plataforma em um chat totalmente integrado',
        image: chat, // Substitua por imagem específica quando disponível
        alt: 'Chat'
    },
    {
        title: 'Ebooks',
        description: 'No Ginastee, você vende também os seus ebooks, contendo dietas, treinos, e o seu método.',
        image: ebooks, // Substitua por imagem específica quando disponível
        alt: 'Ebooks'
    }
];

const INTERVAL_DURATION = 4000; // 4 segundos

export default function Benefits({ containerRef: externalContainerRef }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const sectionRef = useRef(null);
    const containerRef = useRef(null);
    const intervalRef = useRef(null);
    const timeoutRef = useRef(null);

    // Função que avança para o próximo índice
    const advanceIndex = useCallback(() => {
        setActiveIndex((currentIndex) => {
            const nextIndex = currentIndex + 1;

            // Se chegou ao último índice, para o intervalo e agenda reinício
            if (nextIndex >= benefits.length) {
                // Limpa o intervalo atual
                if (intervalRef.current !== null) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }

                // Espera 2 segundos e reinicia no primeiro índice
                timeoutRef.current = setTimeout(() => {
                    setActiveIndex(0);
                }, INTERVAL_DURATION);

                return currentIndex; // Mantém no último durante a espera
            }

            return nextIndex;
        });
    }, []);

    // Função para lidar com o clique em um item
    const handleItemClick = useCallback((clickedIndex) => {
        // Não faz nada se já estiver no índice clicado
        if (clickedIndex === activeIndex) return;

        // Limpa o intervalo e timeout atual
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        setActiveIndex(clickedIndex);

        // Reinicia o intervalo automático após a mudança
        timeoutRef.current = setTimeout(() => {
            // Verifica se não foi clicado novamente antes de reiniciar
            if (intervalRef.current === null) {
                intervalRef.current = setInterval(advanceIndex, INTERVAL_DURATION);
            }
            timeoutRef.current = null;
        }, 900); // Duração da animação (0.9s)
    }, [activeIndex, advanceIndex]);

    useEffect(() => {
        // Limpa intervalos e timeouts anteriores
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
        }
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
        }

        // Inicia o intervalo que muda o índice a cada 2 segundos
        intervalRef.current = setInterval(advanceIndex, INTERVAL_DURATION);

        return () => {
            if (intervalRef.current !== null) {
                clearInterval(intervalRef.current);
            }
            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [advanceIndex]); // Executa quando advanceIndex muda (apenas na montagem)

    // Reinicia o intervalo quando voltar ao índice 0 após pausa no último
    useEffect(() => {
        // Se está no índice 0 e não há intervalo ativo, reinicia o intervalo
        if (activeIndex === 0 && intervalRef.current === null) {
            intervalRef.current = setInterval(advanceIndex, INTERVAL_DURATION);
        }
    }, [activeIndex, advanceIndex]);

    return (
        <div className="benefits" ref={sectionRef}>
            <div className="container" ref={containerRef}>
                <div className="benefits-info pt-7">
                    <Badge
                        className='mt-6'
                        text={<>Tudo que você precisa para seu <span className="text-linear-animated">infoproduto fitness</span></>}
                        leftIcon={<OceanProtocolIcon style={{
                            transform: 'translateY(6px)'
                        }} />}
                    />

                    {benefits.map((benefit, index) => (
                        <div
                            key={index}
                            className={`benefit-box ${activeIndex === index ? 'current' : ''} ${index === 0 ? 'mt-5' : ''}`}
                            style={{ opacity: activeIndex === index ? 1 : 0.35 }}
                            onClick={() => handleItemClick(index)}
                        >
                            <h3 className="mb-1">{benefit.title}</h3>
                            {activeIndex === index && (
                                <span
                                    key={`description-${index}`}
                                    style={{ width: '85%', display: 'block' }}
                                >
                                    {benefit.description}
                                </span>
                            )}
                        </div>
                    ))}

                </div>
                <div className="illustration-container pt-6">
                    <LinesTexture className="lines-texture" />
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={`benefit-image-${activeIndex}`}
                            src={benefits[activeIndex].image}
                            alt={benefits[activeIndex].alt}
                            className="benefit-image"
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 80 }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                        />
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}